import toPairs from "lodash/toPairs";
import { assertNever, panic } from "../../Support";

export enum PropertySection {
    Top = "Top",
    Data = "Data",
    DataTop = "DataTop",
    Design = "Design",
    SignIn = "SignIn",
    Style = "Style",
    Source = "Source",
    Destination = "Destination",
    Events = "Events",
    Search = "Search",
    FilterByUser = "FilterByUser",
    FilterData = "FilterData",
    InAppFilterData = "InAppFilterData",
    Sort = "Sort",
    GroupBy = "GroupBy",
    InAppSortData = "InAppSortData",
    Visibility = "Visibility",
    Add = "Add",
    AddComponents = "AddComponents",
    AddColumns = "AddColumns",
    Edit = "Edit",
    Delete = "Delete",
    EditComponents = "EditComponents",
    EditColumns = "EditColumns",
    Components = "Components",
    CardComponents = "CardComponents",
    Configuration = "Configuration",
    Action = "Action",
    ImportantAction = "ImportantAction",
    OnSubmitAction = "OnSubmitAction",
    LeftAction = "LeftAction",
    RightAction = "RightAction",
    ScanAction = "ScanAction",
    SwipeCondition = "SwipeCondition",
    TitleBarAction = "TitleBarAction",
    CollectionItems = "CollectionItems",
    ItemClick = "ItemClick",
    CollectionItemActions = "CollectionItemActions",
    Options = "Options",
    Columns = "Columns",
    PaymentProcessor = "PaymentProcessor",
    PaymentProductInfo = "PaymentProductInfo",
    Tabs = "Tabs",
    TabsVNext = "TabsVNext",
    Pages = "Pages",
    Screens = "Screens",
    Menu = "Menu",
    HiddenTabs = "HiddenTabs",
    NavigationOptions = "NavigationOptions",
    Template = "Template",
    Replacements = "Replacements",
    QueryParameters = "QueryParameters",
    Cases = "Cases",
    AppURL = "AppURL",
    ShareOptions = "ShareOptions",
    Icon = "Icon",
    AccentColor = "AccentColor",
    Theme = "Theme",
    Theming = "Theming",
    Hidden = "Hidden",
    Title = "Title",
    Rows = "Rows",
    TextStyle = "TextStyle",
    PreviewAs = "PreviewAs",
    ColumnFormat = "ColumnFormat",
    Overlays = "Overlays",
    Reactions = "Reactions",
    Payment = "Payment",
    ScanningOptions = "ScanningOptions",
    UserProfile = "User profile",
    LeftButton = "Left Button",
    RightButton = "Right Button",
    Description = "Description",
}

export function needsPropertySectionInAction(section: SuperPropertySection): section is PropertySection {
    return section === PropertySection.ScanAction || section === PropertySection.ScanningOptions;
}

interface CustomPropertySection {
    name: string;
    order: number;
    header?: HeaderSection;
    needsAddTab?: boolean;
}

export type SuperPropertySection = PropertySection | CustomPropertySection;

const ConfigurationSectionMap = {
    General: [
        PropertySection.TabsVNext,
        PropertySection.Pages,
        PropertySection.Top,
        PropertySection.DataTop,
        PropertySection.UserProfile,
        PropertySection.Style,
        PropertySection.Title,
        PropertySection.Source,
        PropertySection.Destination,
        PropertySection.Events,
        PropertySection.PaymentProcessor,
        PropertySection.Description,
        PropertySection.Data,
        PropertySection.ImportantAction,
        PropertySection.CollectionItems,
        PropertySection.Design,
        PropertySection.LeftButton,
        PropertySection.RightButton,
        PropertySection.PaymentProductInfo,
        PropertySection.TextStyle,
        PropertySection.Components,
        PropertySection.CardComponents,
        PropertySection.Columns,
        PropertySection.Configuration,
        PropertySection.Template,
        PropertySection.Replacements,
        PropertySection.QueryParameters,
        PropertySection.Cases,
        PropertySection.AppURL,
        PropertySection.ShareOptions,
        PropertySection.PreviewAs,
        PropertySection.ColumnFormat,
        PropertySection.Rows,
        PropertySection.Overlays,
        PropertySection.TitleBarAction,
        PropertySection.ItemClick,
        PropertySection.CollectionItemActions,
        PropertySection.Reactions,
        PropertySection.Options,
        PropertySection.Action,
        PropertySection.LeftAction,
        PropertySection.RightAction,
        PropertySection.ScanAction,
        PropertySection.ScanningOptions,
        PropertySection.Delete,
        PropertySection.OnSubmitAction,
    ],
    Options: [
        PropertySection.Visibility,
        PropertySection.Search,
        PropertySection.FilterData,
        PropertySection.Sort,
        PropertySection.GroupBy,
        PropertySection.FilterByUser,
        PropertySection.InAppFilterData,
        PropertySection.InAppSortData,
        PropertySection.SwipeCondition,
    ],
    Design: [PropertySection.Theming, PropertySection.Icon, PropertySection.AccentColor, PropertySection.Theme],
    "Sign-In": [PropertySection.SignIn],
    "Add Form": [PropertySection.Add, PropertySection.AddComponents, PropertySection.AddColumns],
    "Edit Form": [PropertySection.Edit, PropertySection.EditComponents, PropertySection.EditColumns],
    Navigation: [
        PropertySection.Tabs,
        PropertySection.Screens,
        PropertySection.Menu,
        PropertySection.HiddenTabs,
        PropertySection.NavigationOptions,
    ],
    Hidden: [PropertySection.Hidden],
    Payment: [PropertySection.Payment],
};

export function getSectionName(section: SuperPropertySection): string {
    if (typeof section !== "string") {
        return section.name;
    }
    switch (section) {
        case PropertySection.Top:
        case PropertySection.UserProfile:
        case PropertySection.Add:
        case PropertySection.Theming:
        case PropertySection.SignIn:
        case PropertySection.Hidden:
        case PropertySection.Payment:
            return "";
        case PropertySection.Edit:
            return "Edit";
        case PropertySection.Delete:
            return "Delete";
        case PropertySection.Data:
        case PropertySection.DataTop:
            return "Data";
        case PropertySection.Design:
            return "Design";
        case PropertySection.Style:
            return "Style";
        case PropertySection.Source:
            return "Source";
        case PropertySection.Destination:
            return "Destination";
        case PropertySection.Events:
            return "Events";
        case PropertySection.Search:
            return "Search";
        case PropertySection.FilterByUser:
            return "User Data";
        case PropertySection.FilterData:
            return "Filter";
        case PropertySection.InAppFilterData:
            return "In-app Filter";
        case PropertySection.Sort:
            return "Sort";
        case PropertySection.GroupBy:
            return "Group";
        case PropertySection.InAppSortData:
            return "In-app Sort";
        case PropertySection.Visibility:
            return "Visibility";
        case PropertySection.AddComponents:
        case PropertySection.EditComponents:
        case PropertySection.Components:
            return "Screen";
        case PropertySection.CardComponents:
            return "Card";
        case PropertySection.Configuration:
            return "Configuration";
        case PropertySection.Action:
        case PropertySection.ImportantAction:
            return "Action";
        case PropertySection.OnSubmitAction:
            return "On submit";
        case PropertySection.LeftAction:
            return "Left action";
        case PropertySection.RightAction:
            return "Right action";
        case PropertySection.ScanAction:
            return "Scan action";
        case PropertySection.ScanningOptions:
            return "Scanning options";
        case PropertySection.SwipeCondition:
            return "Swipe condition";
        case PropertySection.NavigationOptions:
        case PropertySection.Options:
            return "Options";
        case PropertySection.Columns:
        case PropertySection.EditColumns:
        case PropertySection.AddColumns:
            return "Columns";
        case PropertySection.PaymentProcessor:
            return "Service";
        case PropertySection.PaymentProductInfo:
            return "Product info";
        case PropertySection.Tabs:
        case PropertySection.TabsVNext:
            return "Tabs";
        case PropertySection.Pages:
            return "Pages";
        case PropertySection.Screens:
            return "Tabs";
        case PropertySection.HiddenTabs:
            return "Hidden Tabs";
        case PropertySection.Menu:
            return "Menu";
        case PropertySection.Replacements:
            return "Replacements";
        case PropertySection.QueryParameters:
            return "Query parameters";
        case PropertySection.Template:
            return "Template";
        case PropertySection.Cases:
            return "Cases";
        case PropertySection.AppURL:
            return "App URL";
        case PropertySection.ShareOptions:
            return "Share Options";
        case PropertySection.Icon:
            return "Icon";
        case PropertySection.AccentColor:
            return "Accent Color";
        case PropertySection.Theme:
            return "Theme";
        case PropertySection.Title:
            return "Title";
        case PropertySection.TextStyle:
            return "Text Style";
        case PropertySection.PreviewAs:
            return "View as user";
        case PropertySection.ColumnFormat:
            return "Display";
        case PropertySection.Rows:
            return "Rows";
        case PropertySection.Overlays:
            return "Overlays";
        case PropertySection.Reactions:
            return "Reactions";
        case PropertySection.LeftButton:
            return "Left Button";
        case PropertySection.RightButton:
            return "Right Button";
        case PropertySection.Description:
            return "Description";
        case PropertySection.TitleBarAction:
            return "Title Bar Action";
        case PropertySection.CollectionItems:
            return "Items Data";
        case PropertySection.ItemClick:
            return "Item Click";
        case PropertySection.CollectionItemActions:
            return "Collection Item Actions";
        default:
            assertNever(section);
    }
    return "";
}

export function getSectionPriority(section: SuperPropertySection): number {
    if (typeof section !== "string") {
        return ConfigurationSectionMap.General.length + section.order;
    }
    return Math.max(
        ConfigurationSectionMap.General.indexOf(section),
        ConfigurationSectionMap.Options.indexOf(section),
        ConfigurationSectionMap["Sign-In"].indexOf(section),
        ConfigurationSectionMap["Add Form"].indexOf(section),
        ConfigurationSectionMap["Edit Form"].indexOf(section),
        ConfigurationSectionMap.Navigation.indexOf(section),
        ConfigurationSectionMap.Hidden.indexOf(section)
    );
}

export type HeaderSection = keyof typeof ConfigurationSectionMap;

export const headerSectionOrder: readonly HeaderSection[] = [
    "General",
    "Options",
    "Design",
    "Sign-In",
    "Add Form",
    "Edit Form",
    "Navigation",
    "Hidden",
    "Payment",
];

export function sectionBelongsToHeader(ps: SuperPropertySection, header: HeaderSection): boolean {
    if (typeof ps !== "string") {
        if (ps.header !== undefined) {
            return header === ps.header;
        } else {
            return header === "General";
        }
    }

    const l = ConfigurationSectionMap[header];

    if (l === undefined) return false;

    return l.indexOf(ps) !== -1;
}

export function headerSectionForPropertySection(ps: SuperPropertySection): HeaderSection {
    if (typeof ps !== "string") {
        return ps.header ?? "General";
    }
    for (const [hs, pss] of toPairs(ConfigurationSectionMap)) {
        if (pss.indexOf(ps) >= 0) return hs as HeaderSection;
    }
    return panic(`Property section ${ps} does not exist`);
}
